/**
 * Die Benachrichtigungszentrale (F-30): Glocke mit Zaehler in der oberen
 * Leiste, darunter die Liste. Jede Meldung fuehrt zum Eintrag; sie laesst
 * sich als gelesen markieren, erledigen (eine Aufgabe direkt abhaken) oder
 * verschieben. Am Handy faehrt die Liste von unten hoch (F-42).
 */
import { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Clock, Settings, X } from 'lucide-react';

import { useGlocke } from '@/modules/benachrichtigungen/glockeStore';
import { useMeldungen } from '@/modules/benachrichtigungen/useMeldungen';
import { PushHinweis } from '@/modules/benachrichtigungen/push/PushHinweis';
import { usePushAbgleich } from '@/modules/benachrichtigungen/push/usePushAbgleich';
import { alleGelesen, meldungAendern } from '@/db/repositories/meldungen';
import { toggleTaskDone } from '@/db/repositories/tasks';
import { ARTEN, GRUPPEN } from '@/domain/benachrichtigungen/einstellungen';
import { aktuelle, VERSCHIEBEN_LABEL, verschiebenBis, type Verschieben } from '@/domain/benachrichtigungen/zustand';
import type { Meldung } from '@/domain/benachrichtigungen/regeln';
import { datePart, formatDe } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import { Ausklapper } from '@/ui/composites/Ausklapper';

export function Glocke() {
  const { offen, setOffen } = useGlocke();
  const daten = useMeldungen();
  // Der Push-Plan (F-31) entsteht aus denselben Meldungen - einmal berechnet.
  usePushAbgleich(daten);
  const liste = aktuelle(daten.alle, daten.zustand, daten.jetzt);
  const ungelesen = liste.filter((x) => !x.gelesen).length;
  const knopf = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative">
      <button ref={knopf} type="button" onClick={() => setOffen(!offen)} aria-expanded={offen} aria-haspopup="dialog"
        aria-label={ungelesen > 0 ? `Benachrichtigungen, ${ungelesen} ungelesen` : 'Benachrichtigungen'}
        className="relative grid size-9 shrink-0 place-items-center rounded-md text-ink-muted hover:bg-surface-raised hover:text-ink">
        <Bell className="size-4" />
        {ungelesen > 0 && (
          <span aria-hidden="true" className="absolute right-0.5 top-0.5 grid min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-4 text-on-accent tabular-nums">
            {ungelesen > 9 ? '9+' : ungelesen}
          </span>
        )}
      </button>
      {offen && <Liste daten={daten} liste={liste} knopf={knopf} onClose={() => { setOffen(false); knopf.current?.focus(); }} />}
    </div>
  );
}

function Liste({ daten, liste, knopf, onClose }: {
  daten: ReturnType<typeof useMeldungen>;
  liste: ReturnType<typeof aktuelle>;
  knopf: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}) {
  const ungelesen = liste.filter((x) => !x.gelesen);
  return (
    <Ausklapper label="Benachrichtigungen" ausloeser={knopf} onClose={onClose} className="w-[26rem]">
      <header className="flex items-center gap-2 border-b border-line px-4 py-2.5">
        <h2 className="flex-1 text-sm font-semibold">Benachrichtigungen</h2>
        {ungelesen.length > 0 && (
          <button type="button" onClick={() => void alleGelesen(ungelesen.map(({ m }) => ({ id: m.id, datum: datePart(m.zeit) })))}
            className="inline-flex items-center gap-1 text-[12px] text-ink-muted hover:text-ink"><CheckCheck className="size-3.5" /> Alle gelesen</button>
        )}
        <Link to="/einstellungen#benachrichtigungen" onClick={onClose} aria-label="Einstellungen der Benachrichtigungen"
          className="grid size-7 place-items-center rounded-md text-ink-faint hover:bg-surface-raised hover:text-ink"><Settings className="size-3.5" /></Link>
        <button type="button" onClick={onClose} aria-label="Schließen" className="grid size-7 place-items-center rounded-md text-ink-faint hover:bg-surface-raised hover:text-ink"><X className="size-3.5" /></button>
      </header>
      <PushHinweis />
      <ul className="min-h-0 flex-1 overflow-y-auto">
        {liste.length === 0 && <li className="px-4 py-8 text-center text-sm text-ink-faint">Nichts Neues.</li>}
        {liste.map(({ m, gelesen }) => <Eintrag key={m.id} m={m} gelesen={gelesen} daten={daten} onClose={onClose} />)}
      </ul>
    </Ausklapper>
  );
}

function Eintrag({ m, gelesen, daten, onClose }: { m: Meldung; gelesen: boolean; daten: ReturnType<typeof useMeldungen>; onClose: () => void }) {
  const navigate = useNavigate();
  const datum = datePart(m.zeit);
  const info = ARTEN.find((a) => a.art === m.art)!;
  const heute = datum === daten.jetzt.slice(0, 10);
  const verschieben = (wie: Verschieben) => void meldungAendern(m.id, datum, {
    verschobenBis: verschiebenBis(wie, daten.jetzt, daten.einstellungen.ruheVon, daten.einstellungen.ruheBis), gelesen: 0,
  });
  return (
    <li className={cn('flex flex-col gap-1 border-b border-line px-4 py-2.5 last:border-b-0', !gelesen && 'bg-accent/[0.05]')}>
      <div className="flex items-start gap-2">
        {!gelesen && <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" aria-label="ungelesen" />}
        <button type="button" className="min-w-0 flex-1 text-left" onClick={() => { void meldungAendern(m.id, datum, { gelesen: 1 }); onClose(); navigate(m.link); }}>
          <span className="block text-[11px] text-ink-faint">{GRUPPEN[info.gruppe]} · {heute ? formatDe(m.zeit, 'HH:mm') : formatDe(m.zeit, 'EEEEEE dd.MM. HH:mm')}</span>
          <span className={cn('block text-sm', gelesen ? 'text-ink-muted' : 'font-medium text-ink')}>{m.titel}</span>
          {m.text && <span className="block text-[12px] text-ink-muted">{m.text}</span>}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1 pl-3.5 text-[11px]">
        {/*
          Nur abhaken: Meldungen zu Aufgaben (Zeitblock, ueberfaellig) entstehen
          aus offenen Aufgaben und verschwinden damit von selbst. Ein eigenes
          „erledigt“ an der Meldung bliebe beim Rueckgaengig (F-45) stehen und
          verschluckte die Erinnerung an die wieder offene Aufgabe.
        */}
        {m.aufgabeId && (
          <button type="button" onClick={() => void toggleTaskDone(m.aufgabeId!)}
            className="inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-ink-muted hover:text-ink"><Check className="size-3" /> Aufgabe abhaken</button>
        )}
        <button type="button" onClick={() => void meldungAendern(m.id, datum, { erledigt: 1 })}
          className="rounded border border-line px-1.5 py-0.5 text-ink-muted hover:text-ink">Erledigt</button>
        {(['1h', 'morgen'] as const).map((w) => (
          <button key={w} type="button" onClick={() => verschieben(w)} aria-label={`„${m.titel}“ verschieben: ${VERSCHIEBEN_LABEL[w]}`}
            className="inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-ink-muted hover:text-ink"><Clock className="size-3" /> {VERSCHIEBEN_LABEL[w]}</button>
        ))}
        {!gelesen && (
          <button type="button" onClick={() => void meldungAendern(m.id, datum, { gelesen: 1 })} className="ml-auto text-ink-faint hover:text-ink">Gelesen</button>
        )}
      </div>
    </li>
  );
}
